import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Fade from '@mui/material/Fade';
import { type FC, useCallback, useState } from 'react';
import { useValidateRutMutation } from 'services/api/insuranceQuoteForm.api';
import type { PersonInfo, ValidateRutResponse } from 'services/types/insuranceQuoteForm';
import { RADIUS_COIN } from 'styles/tokens';

import { SecondaryButton } from '../atoms/FormButtons';
import RutField from '../atoms/RutField';
import InlineFeedback from './InlineFeedback';

const WARNING_COPY: Record<NonNullable<ValidateRutResponse['warning']>, string> = {
  foreign_rut: 'Parece ser un RUT extranjero. Verifica si tiene residencia definitiva.',
  company_rut: 'Este es un RUT de empresa.',
  test_rut: 'Este es un RUT de prueba.',
};

interface ValidatedRutSearchProps {
  value: string;
  onChange: (rut: string) => void;
  onValidated: (result: { formattedRut: string; personInfo: PersonInfo | null }) => void;
  setIsLoading?: (loading: boolean) => void;
  validatedPerson?: PersonInfo | null;
}

const ValidatedRutSearch: FC<ValidatedRutSearchProps> = ({
  value,
  onChange,
  onValidated,
  setIsLoading,
  validatedPerson,
}) => {
  const [validateRut] = useValidateRutMutation();
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [validated, setValidated] = useState(!!validatedPerson);

  const handleSearch = useCallback(async () => {
    if (!value.trim()) {
      setError('Por favor ingresa un RUT');
      return;
    }
    setError(null);
    setWarning(null);
    setIsLoading?.(true);
    try {
      const result = await validateRut({ rut: value }).unwrap();
      if (!result.isValid) {
        setError(result.error || 'RUT inválido');
        setValidated(false);
        return;
      }
      if (result.warning) setWarning(WARNING_COPY[result.warning]);
      onValidated({
        formattedRut: result.formattedRut || value,
        personInfo: result.personInfo || null,
      });
      setValidated(true);
    } catch {
      setError('Error al validar el RUT. Por favor intenta nuevamente.');
    } finally {
      setIsLoading?.(false);
    }
  }, [value, validateRut, onValidated, setIsLoading]);

  const handleChange = useCallback(
    (next: string) => {
      onChange(next);
      setValidated(false);
      setError(null);
      setWarning(null);
    },
    [onChange]
  );

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
          alignItems: 'flex-start',
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
        }}
      >
        <RutField
          value={value}
          onChange={handleChange}
          error={error}
          required
          sx={{ flex: '1 1 220px', maxWidth: { sm: 280 } }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void handleSearch();
            }
          }}
        />
        <SecondaryButton
          onClick={() => void handleSearch()}
          disabled={!value.trim()}
          sx={{ height: 40, alignSelf: 'flex-start', mt: '4px' }}
        >
          Buscar
        </SecondaryButton>
        {validated && validatedPerson && (
          <Fade in>
            <Chip
              icon={<CheckCircleOutlinedIcon />}
              label={`${validatedPerson.firstName} ${validatedPerson.lastName}`}
              sx={{
                fontFamily: 'Jost, sans-serif',
                fontWeight: 600,
                height: 40,
                borderRadius: RADIUS_COIN,
                bgcolor: 'rgba(37, 97, 111, 0.08)',
                color: '#25616F',
                '& .MuiChip-icon': { color: '#25616F' },
              }}
            />
          </Fade>
        )}
        {validated && !validatedPerson && (
          <Fade in>
            <Chip
              icon={<CheckCircleOutlinedIcon />}
              label="RUT válido"
              sx={{
                fontFamily: 'Jost, sans-serif',
                fontWeight: 600,
                height: 40,
                borderRadius: RADIUS_COIN,
                bgcolor: 'rgba(44, 88, 137, 0.08)',
                color: '#2C5889',
                '& .MuiChip-icon': { color: '#2C5889' },
              }}
            />
          </Fade>
        )}
      </Box>
      {warning && (
        <InlineFeedback severity="warning" sx={{ mt: 2 }}>
          {warning}
        </InlineFeedback>
      )}
    </Box>
  );
};

export default ValidatedRutSearch;
